import { Component, ElementRef, OnInit, ViewChild, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, Select, Actions, ofActionSuccessful } from '@ngxs/store';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { SetSpinner } from '@core/store/shared.actions';
import { AlertDialogType } from '@shared/models';
import { DialogHandlerService } from '@core/services/dialog-handler.service';
import { MatDialog } from '@angular/material/dialog';
import { DynamicFormService } from '../../../modules/dynamic-form/services/dynamic-form.service';
import { LoadJobFile } from '../../../modules/load-management/store/load-management.actions';
import { LoadManagementState } from '../../../modules/load-management/store/load-management.state';
import { DuplicateOrdersDialogComponent } from '../../../modules/wizard/components/duplicate-orders-dialog/duplicate-orders-dialog.component';
import { OrderManagementService } from '../../../modules/order-management/services/order-management.service';
import { ConfirmationDialogComponent, ConfirmationDialogData, ConfirmationDialogResult } from '../confirmation-dialog/confirmation-dialog.component';
import { AuthState } from '@core/store/auth/auth.state';
import { JobFile } from '@core/models/job-file/job-file.model';
import { ShipmentOrderSpec } from '@core/models/job-file/shipment-order-spec.model';
import { JobFileContainer } from '@core/models/job-file/job-file-container.model';
import { OrderSkuQuantity, ItemQuantity, SpecGroup, AssignAllRequest } from '@core/models/job-file';
import { Observable, Subject } from 'rxjs';
import { take, switchMap, filter, tap, catchError, takeUntil, debounceTime } from 'rxjs/operators';
import { of } from 'rxjs';
import { JobPlanOrderDisplayComponent, PanelQuery } from '../job-plan-order-display/job-plan-order-display.component';
import { OrderFilters } from '../order-filters/order-filters.component';
import { JobFileService } from '@core/services/job-file.service';
import { OrderListPopup } from '../../../modules/dynamic-form/components/order-list-result/order-list-result.compoent';

@Component({
  selector: 'hmt-job-plan-order-list',
  templateUrl: './job-plan-order-list.component.html',
  standalone: false,
  styleUrls: ['./job-plan-order-list.component.scss']
})
export class JobPlanOrderListComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('uploadInput', { read: ElementRef }) uploadInput: ElementRef;
  @ViewChild('orderDisplayComponent', { read: JobPlanOrderDisplayComponent }) ordersDisplayComponent: JobPlanOrderDisplayComponent;
  @Select(LoadManagementState.jobFile) jobFile$: Observable<JobFile | null>;
  @Select(LoadManagementState.isLoading) loading$: Observable<boolean>;
  @Input() caption: string = 'Please upload your order files or select from existing orders';
  @Input() jobType?: string;
  @Input() isReadOnly = false;
  private unsubscribe: Subject<void> = new Subject();
  jobRefId: string;
  orgId: string;
  orders: ShipmentOrderSpec[] = [];
  showOnlySelected: boolean = false;
  originalOrders: ShipmentOrderSpec[] = [];
  containers: JobFileContainer[] = [];
  /** Locked loads (scheduled / in progress / sent as SR or RFQ) can't receive moved orders. */
  @Input() lockedContainerIds: Set<string> | null = null;
  /** Move dropdown options: containers minus locked loads. */
  moveTargetContainers: JobFileContainer[] = [];
  selectedLoadId: string = '';
  jobFile: JobFile | null = null;

  // Server-side pagination
  pageIndex = 0;
  pageSize = 20;
  totalElements = 0;
  grouped = false;
  serverGroups: SpecGroup[] = [];
  loadingPage = false;
  // Current search/sort/group/filter query (from the display controls) driving the server fetch.
  private query: PanelQuery = { searchText: '', sortBy: 'totalVolume', sortOrder: 'ascending', groupBy: 'none', otherFilters: null };
  private queryChanged$ = new Subject<PanelQuery>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store,
    private actions: Actions,
    private dialogHandlerService: DialogHandlerService,
    private dialog: MatDialog,
    private dynamicFormService: DynamicFormService,
    private orderManagementService: OrderManagementService,
    private jobFileService: JobFileService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lockedContainerIds']) {
      this.refreshMoveTargets();
    }
  }

  private refreshMoveTargets(): void {
    const locked = this.lockedContainerIds;
    this.moveTargetContainers = locked?.size
      ? this.containers.filter(c => !locked.has(c.containerId))
      : this.containers;
    // Selected target got locked: clear it so Move can't send orders there.
    if (this.selectedLoadId && !this.moveTargetContainers.some(c => c.containerId === this.selectedLoadId)) {
      this.selectedLoadId = '';
    }
  }

  ngOnInit(): void {
    // Get orgId from store
    this.orgId = this.store.selectSnapshot(AuthState.getOrgId);

    // Get jobRefId from query params
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        tap(params => {
          if (params.jobRefId) {
            this.jobRefId = params.jobRefId.split('?')[0]; // Clean jobRefId
            // Keep a read-only state set by the parent (e.g. a terminated job); a shared job is read-only too.
            this.isReadOnly = this.isReadOnly || params?.['shared'] === 'true';
            // Load job file if jobRefId exists
            if (this.jobRefId) {
              this.store.dispatch(new LoadJobFile(this.orgId, this.jobRefId));
            }
          }
        })
      )
      .subscribe();

    // Subscribe to job file changes for containers + workflow metadata. Shipment order specs are no
    // longer embedded here — the panel fetches them server-side paginated via loadPage().
    this.jobFile$
      .pipe(
        takeUntil(this.unsubscribe),
        tap(jobFile => {
          this.jobFile = jobFile;
          this.containers = (jobFile && jobFile.containers) ? jobFile.containers : [];
          this.refreshMoveTargets();
        })
      )
      .subscribe();

    // Debounce control changes (search typing) before hitting the server.
    this.queryChanged$
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe(query => {
        this.query = query;
        this.pageIndex = 0;
        this.loadPage();
      });

    // Refresh the current page after any job-file mutation (move / remove / delete / upload).
    this.actions
      .pipe(
        ofActionSuccessful(LoadJobFile),
        takeUntil(this.unsubscribe),
        tap(() => this.loadPage())
      )
      .subscribe();

    // Initial page load (jobRefId is resolved synchronously from queryParams above via take(1)).
    if (this.jobRefId) {
      this.loadPage();
    }
  }

  /** Fetch the current page of orders from the server for the active query. */
  private loadPage(): void {
    if (!this.jobRefId) {
      return;
    }
    this.loadingPage = true;
    const f: OrderFilters | null = this.query.otherFilters;
    this.jobFileService.getShipmentOrderSpecsPanel(this.orgId, this.jobRefId, {
      page: this.pageIndex,
      size: this.pageSize,
      sortBy: this.query.sortBy,
      sortOrder: this.query.sortOrder,
      groupBy: this.query.groupBy,
      searchText: this.query.searchText,
      deliveryOnOrBefore: f?.deliveryOnOrBeforeDate ? new Date(f.deliveryOnOrBeforeDate).getTime() : undefined,
      deliveryOnOrAfter: f?.deliveryOnOrAfterDate ? new Date(f.deliveryOnOrAfterDate).getTime() : undefined,
      weightMin: f?.weightMin,
      weightMax: f?.weightMax,
      cbmMin: f?.capacityMin,
      cbmMax: f?.capacityMax
    }).pipe(
      take(1),
      takeUntil(this.unsubscribe),
      catchError(() => {
        this.loadingPage = false;
        return of(null);
      })
    ).subscribe(res => {
      this.loadingPage = false;
      if (!res) {
        return;
      }
      this.grouped = res.grouped;
      this.serverGroups = res.groups || [];
      this.orders = res.grouped ? (res.groups || []).flatMap(g => g.orders) : (res.orders || []);
      this.totalElements = res.totalElements;
    });
  }

  /** Display controls changed (search/sort/group/other-filters) → re-query the server. */
  onQueryChange(query: PanelQuery): void {
    this.queryChanged$.next(query);
  }

  /** Paginator page change. */
  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadPage();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  selectOrders(): void {
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        tap(params => {
          // Get the latest jobFile from store to ensure we have the most up-to-date data
          const currentJobFile = this.store.selectSnapshot(LoadManagementState.jobFile) || this.jobFile;
          
          // Get workflowId, shipper, and pickupLocationId from jobFile
          let workflowId: string | undefined;
          let shipper: string | undefined;
          let pickupLocationId: string | undefined;
          
          if (currentJobFile) {
            workflowId = currentJobFile.workflowId || currentJobFile.workFlowId;
            
            // Try to get shipper and pickupLocationId from multiple sources
            // First try from jobFile.shipmentOrderSpecs
            let firstOrder: ShipmentOrderSpec | null = null;
            if (currentJobFile.shipmentOrderSpecs && currentJobFile.shipmentOrderSpecs.length > 0) {
              firstOrder = currentJobFile.shipmentOrderSpecs[0] as ShipmentOrderSpec;
            }
            // Fallback to originalOrders if jobFile.shipmentOrderSpecs is empty
            else if (this.originalOrders && this.originalOrders.length > 0) {
              firstOrder = this.originalOrders[0];
            }
            // Fallback to orders if originalOrders is also empty
            else if (this.orders && this.orders.length > 0) {
              firstOrder = this.orders[0];
            }
            
            // Extract shipper and pickupLocationId from the first order if found
            // Note: The backend uses shipperShortName and pickupLocationShortName,
            // but the TypeScript model maps them to shipper and pickupLocationId
            if (firstOrder) {
              // Try both field name variations to handle different data structures
              shipper = (firstOrder as any).shipper || (firstOrder as any).shipperShortName;
              pickupLocationId = (firstOrder as any).pickupLocationId || (firstOrder as any).pickupLocationShortName;
              
            } else {
              console.warn('No orders found to extract shipper and pickupLocationId from');
            }
          }
          
          // Add parameters to indicate we came from Manual Plan and include workflow filtering data
          const navigationParams: any = {
            ...params,
            returnToManualPlan: 'true'
          };
          
          if (workflowId) {
            navigationParams.workflowId = workflowId;
          }
          if (shipper) {
            navigationParams.shipper = shipper;
          }
          if (pickupLocationId) {
            navigationParams.pickupLocationId = pickupLocationId;
          }
          
          this.dynamicFormService.navigateTo(
            '/order-management/pending',
            navigationParams
          );
        })
      )
      .subscribe();
  }

  openFileUpload(): void {
    if (this.uploadInput) {
      this.uploadInput.nativeElement.click();
    }
  }

  onSelectImportDocument(event: any): void {
    const fileList: FileList = event.target.files;
    const files = Array.from(fileList);

    if (files.length === 0) {
      return;
    }

    if (files.length > 1) {
      this.store.dispatch(new SetNotificationAction('Only one file can be uploaded', AlertDialogType.ERROR));
      event.target.value = '';
      return;
    }

    const file = files[0];

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      this.store.dispatch(new SetNotificationAction('Please upload an Excel file (.xlsx or .xls)', AlertDialogType.ERROR));
      event.target.value = '';
      return;
    }

    this.store.dispatch(new SetSpinner(true));

    // Upload to Order Management first to get orders with IDs, then upload to Job File
    this.uploadToOrderManagementFirst(file);

    event.target.value = '';
  }

  private uploadToOrderManagementFirst(file: File): void {
    const dialogConfig = {
      height: '200px',
      width: '400px',
    };
    
    if (!this.jobRefId) {
      console.error('No jobRefId found');
      this.store.dispatch(new SetSpinner(false));
      this.dialogHandlerService.openConfirmationDialog(
        'UNABLE TO PROCESS ORDER FILE',
        'Job reference ID is missing. Please try again.',
        AlertDialogType.ERROR,
        true,
        dialogConfig
      );
      return;
    }
    
    try {
      // First, check for duplicate order IDs
      this.orderManagementService.checkDuplicateOrderIds(file, this.orgId, this.jobRefId)
        .pipe(
          take(1),
          switchMap((duplicateOrderIds) => {
            
            // If duplicates are found, show custom dialog with Cancel/Continue options
            if (duplicateOrderIds && duplicateOrderIds.length > 0) {
              this.store.dispatch(new SetSpinner(false));
              
              // Open custom duplicate orders dialog
              const dialogRef = this.dialog.open(DuplicateOrdersDialogComponent, {
                width: '600px',
                maxWidth: '90vw',
                disableClose: true,
                data: {
                  duplicateOrderIds: duplicateOrderIds
                }
              });
              
              // Wait for user's choice
              return dialogRef.afterClosed().pipe(
                take(1),
                switchMap((result) => {
                  if (result === true) {
                    // User clicked Continue, proceed with upload despite duplicates
                    this.store.dispatch(new SetSpinner(true));
                    return this.orderManagementService.uploadShipmentOrderExcelWithJob(file, this.orgId, this.jobRefId);
                  } else {
                    // User clicked Cancel, stop the flow
                    return of(null);
                  }
                })
              );
            }
            
            // No duplicates, proceed with upload
            return this.orderManagementService.uploadShipmentOrderExcelWithJob(file, this.orgId, this.jobRefId);
          }),
          filter(uploadedOrders => uploadedOrders !== null), // Filter out null (when user cancelled)
          tap((uploadedOrders) => {
            // Handle error report (ArrayBuffer) - show error dialog and exit
            if (uploadedOrders instanceof ArrayBuffer) {
              this.store.dispatch(new SetSpinner(false));
              this.dialogHandlerService.openDialog(OrderListPopup,
                { errorReportData: uploadedOrders },
                {
                  height: '280px',
                  width: '550px',
                  position: {},
                });
              return;
            }

            // Now upload these orders to the job file
            this.dynamicFormService.uploadOrdersWithIdsToJobFile(
              this.orgId,
              this.jobRefId,
              uploadedOrders
            ).subscribe(
              (response) => {
                this.store.dispatch(new SetSpinner(false));

                // Reload job file data
                this.store.dispatch(new LoadJobFile(this.orgId, this.jobRefId));

                // Show success dialog
                this.dialogHandlerService.openConfirmationDialog(
                  'ORDER FILE PROCESSED',
                  'Order file has been processed successfully.',
                  AlertDialogType.SUCCESS,
                  true,
                  dialogConfig
                );
              },
              (jobFileError) => {
                console.error('Error uploading orders to Job File:', jobFileError);
                this.store.dispatch(new SetSpinner(false));
                this.dialogHandlerService.openConfirmationDialog(
                  'UNABLE TO ADD ORDERS TO JOB FILE',
                  'Orders were processed but failed to add to job file: ' + (jobFileError.message || 'Unknown error'),
                  AlertDialogType.ERROR,
                  true,
                  dialogConfig
                );
              }
            );
          }),
          catchError(error => {
            console.error('Error during file processing:', error);
            this.store.dispatch(new SetSpinner(false));
            this.dialogHandlerService.openConfirmationDialog(
              'UNABLE TO PROCESS ORDER FILE',
              'Order file upload failed',
              AlertDialogType.ERROR,
              true,
              dialogConfig
            );
            return of(null);
          })
        )
        .subscribe();
    } catch (error) {
      console.error('Error uploading file:', error);
      this.store.dispatch(new SetSpinner(false));
      this.dialogHandlerService.openConfirmationDialog(
        'UNABLE TO PROCESS ORDER FILE',
        'Order file upload failed',
        AlertDialogType.ERROR,
        true,
        dialogConfig
      );
    }
  }

  onMoveClick(): void {
    if (!this.ordersDisplayComponent) {
      return;
    }

    if (this.showOnlySelected) {
      // Exit move-review mode → reload the current server page.
      this.showOnlySelected = false;
      this.loadPage();
      return;
    }

    const enterReview = () => {
      this.showOnlySelected = true;
      if (this.ordersDisplayComponent.isSelectAll) {
        // Select-all: can't materialise every order client-side; keep the current page visible for
        // context — the actual move is resolved server-side on load pick.
        return;
      }
      // Explicit selection: review just the picked orders (from the snapshot), flat + expanded.
      this.grouped = false;
      this.serverGroups = [];
      this.orders = this.ordersDisplayComponent.getSelectedOrdersWithSelectedSkus();
      this.autoExpandAllGroups();
    };

    const workflowId = this.jobFile?.workflowId || this.jobFile?.workFlowId;
    if (workflowId === 'LF-DI-FTL') {
      const dialogData: ConfirmationDialogData = {
        message: 'Manually adding orders may affect the optimised result. Do you really want to move the order?',
        showCheckbox: false,
        imagePath: 'assets/icons/defaultIcon.png'
      };
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, { width: '800px', data: dialogData });
      dialogRef.afterClosed().pipe(take(1)).subscribe((result: ConfirmationDialogResult) => {
        if (result && result.buttonPressed === 'yes') {
          enterReview();
        }
      });
    } else {
      enterReview();
    }
  }

  private autoExpandAllGroups(): void {
    if (this.ordersDisplayComponent) {
      setTimeout(() => {
        this.ordersDisplayComponent.groupedOrders.forEach(group => {
          this.ordersDisplayComponent.expandedGroupKeys.add(group.groupKey);
        });
      }, 0);
    }
  }

  hasSelectedOrders(): boolean {
    if (!this.ordersDisplayComponent) {
      return false;
    }
    return this.ordersDisplayComponent.hasSelectedOrdersOrItems();
  }

  /** Builds the panel filter request (current search/date/weight/cbm) plus the given exclusions. */
  private buildPanelRequest(excludeOrderIds: string[]): AssignAllRequest {
    const f: OrderFilters | null = this.query.otherFilters;
    return {
      searchText: this.query.searchText,
      deliveryOnOrBefore: f?.deliveryOnOrBeforeDate ? new Date(f.deliveryOnOrBeforeDate).getTime() : undefined,
      deliveryOnOrAfter: f?.deliveryOnOrAfterDate ? new Date(f.deliveryOnOrAfterDate).getTime() : undefined,
      weightMin: f?.weightMin,
      weightMax: f?.weightMax,
      cbmMin: f?.capacityMin,
      cbmMax: f?.capacityMax,
      excludeOrderIds
    };
  }

  onDeleteSelected(): void {
    if (!this.ordersDisplayComponent) {
      console.error('Order display component not available');
      return;
    }
    if (!this.jobRefId) {
      console.error('No jobRefId found');
      this.store.dispatch(new SetNotificationAction('Job reference ID is missing. Please try again.', AlertDialogType.ERROR));
      return;
    }

    const ctx = this.ordersDisplayComponent.getMoveContext();

    // Resolve the spec ids to delete. Explicit selection → from the in-memory snapshot; select-all →
    // all matching ids across pages (minus exclusions), resolved server-side.
    const ids$: Observable<string[]> = ctx.isSelectAll
      ? this.jobFileService.getShipmentOrderSpecsPanelIds(this.orgId, this.jobRefId, this.buildPanelRequest(ctx.excludeOrderIds))
      : of(ctx.orders.map(order => order.id).filter((id): id is string => id != null && id !== ''));

    ids$.pipe(take(1), takeUntil(this.unsubscribe)).subscribe(shipmentOrderIds => {
      if (!shipmentOrderIds || shipmentOrderIds.length === 0) {
        this.store.dispatch(new SetNotificationAction('No orders selected to delete', AlertDialogType.WARNING));
        return;
      }

      const dialogData: ConfirmationDialogData = {
        message: 'Are you sure you want to remove the selected order(s)?',
        showCheckbox: true,
        checkboxText: 'Remove these orders from Order Manager as well',
        imagePath: 'assets/icons/defaultIcon.png'
      };

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, { width: '800px', data: dialogData });

      dialogRef.afterClosed().pipe(
        take(1),
        filter((result: ConfirmationDialogResult) => result && result.buttonPressed === 'yes'),
        tap(() => this.store.dispatch(new SetSpinner(true))),
        switchMap((result: ConfirmationDialogResult) => {
          const shouldDeleteFromOrderManager = result.checkboxChecked || false;
          return this.jobFileService.deleteBatchShipmentOrderSpecsFromJobFile(
            this.jobRefId,
            shipmentOrderIds,
            shouldDeleteFromOrderManager
          );
        }),
        tap(() => {
          if (this.ordersDisplayComponent) {
            this.ordersDisplayComponent.clearSelectionState();
          }
          // Same as move: the deleted orders were the filter's matches, so drop the filter before
          // the reload or the panel comes back empty.
          this.resetPanelQuery();
          this.store.dispatch(new LoadJobFile(this.orgId, this.jobRefId));
          this.store.dispatch(new SetSpinner(false));
          this.store.dispatch(new SetNotificationAction(
            `Successfully deleted ${shipmentOrderIds.length} order(s)`,
            AlertDialogType.SUCCESS
          ));
        }),
        catchError((error) => {
          console.error('Error deleting orders:', error);
          this.store.dispatch(new SetSpinner(false));
          this.store.dispatch(new SetNotificationAction(
            'Failed to delete orders: ' + (error.error?.message || error.message || 'Unknown error'),
            AlertDialogType.ERROR
          ));
          return of(null);
        })
      ).subscribe();
    });
  }

  get visibleOrdersCount(): number {
    // Total matching orders across all pages (server-provided), for the empty-state gate + counts.
    return this.totalElements;
  }

  /**
   * True when a search string or an "other filters" selection is narrowing the server query.
   * The empty-state gate uses this so a filtered zero-result keeps the display (and therefore the
   * search / filter controls) mounted — otherwise the user is stranded with no way to clear the
   * filter that is hiding the remaining orders.
   */
  get hasActiveQuery(): boolean {
    return !!this.query.searchText || this.query.otherFilters != null;
  }

  /**
   * Clears the search / other-filters part of the panel query (sort + group are user preferences
   * and are kept) and rewinds to the first page. Used after a mutation that removes the orders the
   * filter was matching, so the panel comes back showing the remaining orders.
   * Does not fetch — the caller triggers the reload.
   */
  private resetPanelQuery(): void {
    this.query = { ...this.query, searchText: '', otherFilters: null };
    this.pageIndex = 0;
    if (this.ordersDisplayComponent) {
      this.ordersDisplayComponent.resetFilters();
    }
  }

  get selectedOrdersCount(): number {
    if (!this.ordersDisplayComponent) {
      return 0;
    }
    return this.ordersDisplayComponent.getSelectedCount();
  }

  onLoadSelected(loadId: string): void {
    // Locked loads are filtered out of the dropdown; this guards a selection made before the lock.
    if (loadId && this.lockedContainerIds?.has(loadId)) {
      this.selectedLoadId = '';
      return;
    }
    this.selectedLoadId = loadId;

    if (!loadId) {
      return;
    }
    if (!this.ordersDisplayComponent) {
      console.error('Order display component not available');
      return;
    }

    const ctx = this.ordersDisplayComponent.getMoveContext();

    let assign$: Observable<void>;
    if (ctx.isSelectAll) {
      // Select-all across all pages → resolved server-side from the current filter (minus exclusions).
      const f: OrderFilters | null = this.query.otherFilters;
      const request: AssignAllRequest = {
        searchText: this.query.searchText,
        deliveryOnOrBefore: f?.deliveryOnOrBeforeDate ? new Date(f.deliveryOnOrBeforeDate).getTime() : undefined,
        deliveryOnOrAfter: f?.deliveryOnOrAfterDate ? new Date(f.deliveryOnOrAfterDate).getTime() : undefined,
        weightMin: f?.weightMin,
        weightMax: f?.weightMax,
        cbmMin: f?.capacityMin,
        cbmMax: f?.capacityMax,
        excludeOrderIds: ctx.excludeOrderIds
      };
      assign$ = this.dynamicFormService.assignAllShipmentOrdersToContainer(this.orgId, this.jobRefId, loadId, request);
    } else {
      if (ctx.orders.length === 0) {
        this.store.dispatch(new SetNotificationAction('No orders selected to move', AlertDialogType.WARNING));
        return;
      }
      const orderSkuQuantities: OrderSkuQuantity[] = ctx.orders.map(order => {
        const itemQuantities: ItemQuantity[] = (order.shipmentOrders || []).map(sku => ({
          itemRefNumber: sku.itemRefId,
          quantity: sku.quantity
        }));
        return { orderId: order.orderId, itemQuantities };
      });
      assign$ = this.dynamicFormService.assignShipmentOrdersToContainer(this.orgId, this.jobRefId, loadId, orderSkuQuantities);
    }

    this.store.dispatch(new SetSpinner(true));
    assign$.pipe(
      take(1),
      tap(() => {
        // Success - clear selection, exit review mode, refresh (LoadJobFile success → loadPage()).
        if (this.ordersDisplayComponent) {
          this.ordersDisplayComponent.clearSelectionState();
        }
        this.showOnlySelected = false;
        this.selectedLoadId = '';
        // The moved orders were the ones matching the active search/filter, so leaving the filter on
        // would refetch an empty page and hide the remaining orders. Clear it before the reload.
        this.resetPanelQuery();
        this.store.dispatch(new LoadJobFile(this.orgId, this.jobRefId));
        this.store.dispatch(new SetSpinner(false));
      }),
      catchError((error) => {
        console.error('Error assigning orders to container:', error);
        this.store.dispatch(new SetSpinner(false));
        this.store.dispatch(new SetNotificationAction(
          'Failed to assign orders to container: ' + (error.error?.message || error.message || 'Unknown error'),
          AlertDialogType.ERROR
        ));
        return of(null);
      })
    ).subscribe();
  }
}
